import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SECTIONS = [
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'navigation', title: 'Navigation & Account' },
  { id: 'dashboard', title: 'Home Dashboard' },
  { id: 'community', title: 'Community & Communication' },
  { id: 'hoa', title: 'HOA Records & Documents' },
  { id: 'access', title: 'Gate Access & Mail' },
  { id: 'monitoring', title: 'Monitoring & Environment' },
  { id: 'identification', title: 'Identification Tools' },
  { id: 'maps', title: 'Maps, Aviation & Night Sky' },
  { id: 'media', title: 'Photos & Video' },
  { id: 'mobile', title: 'Mobile App' },
  { id: 'support', title: 'Support' },
];

function HelpPage({ isMobile }) {
  useEffect(() => {
    document.title = 'Help | Nantahala Mountain Resort';
    return () => {
      document.title = 'NMR Community';
    };
  }, []);

  return (
    <div className={`docs-page ${isMobile ? 'docs-page-mobile' : ''}`}>
      <article className="docs-card docs-card-wide">
        <header className="docs-header">
          <h1 className="docs-title">Owner Help Guide</h1>
          <p className="docs-meta">
            Nantahala Mountain Resort Community Portal
            <br />
            How to use every feature of the website and mobile app
          </p>
        </header>

        <nav className="docs-toc" aria-label="Help sections">
          <h2 className="docs-toc-title">On this page</h2>
          <ul>
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ul>
        </nav>

        <section id="getting-started" className="docs-section">
          <h2>Getting Started</h2>
          <p>
            The Nantahala Mountain Resort (NMR) portal is for verified homeowners and authorized community
            members. Most features require you to sign in with the email address linked to your lot in the HOA
            records.
          </p>
          <h3>Signing in</h3>
          <ol>
            <li>From the main page, choose <strong>Owner&apos;s Login</strong>.</li>
            <li>Enter your email and password. If you forgot your password, use the reset link on the login form.</li>
            <li>After login you are taken to your <Link to="/dashboard" className="docs-link">Home dashboard</Link>.</li>
          </ol>
          <h3>Public vs. owner pages</h3>
          <p>
            Before login you can browse the neighborhood map, rentals list, and privacy policy. Everything else —
            invoices, gate access, mailbox alerts, message board, polls, and monitoring tools — requires an owner
            account.
          </p>
        </section>

        <section id="navigation" className="docs-section">
          <h2>Navigation & Account</h2>
          <h3>Hamburger menu</h3>
          <p>
            Tap the menu icon (top left) to open the full site directory. Links are sorted alphabetically with
            <strong> Home</strong> pinned at the top. Use this menu to jump to any feature.
          </p>
          <h3>Preferences</h3>
          <p>
            Tap the gear icon next to your email in the header to open{' '}
            <Link to="/preferences" className="docs-link">Preferences</Link>. There you can configure email
            notification settings for community updates, message board replies, file uploads, and emergency
            broadcasts.
          </p>
          <h3>Logging out</h3>
          <p>
            On desktop, use the <strong>Logout</strong> button in the header. On mobile, logout appears at the
            bottom of the hamburger menu.
          </p>
        </section>

        <section id="dashboard" className="docs-section">
          <h2>Home Dashboard</h2>
          <p>
            <Link to="/dashboard" className="docs-link">Home</Link> is your landing page after login. It combines:
          </p>
          <ul>
            <li><strong>Weather</strong> — current conditions and a short historical chart for the resort area.</li>
            <li><strong>Local news</strong> — Western North Carolina headlines from curated RSS feeds.</li>
            <li><strong>Upcoming events</strong> — community calendar highlights and regional happenings.</li>
            <li><strong>Mail delivery summary</strong> — when Ring mailbox monitoring is active, a compact status of recent deliveries.</li>
          </ul>
          <p>
            Weather and news data refresh when you load the page. Admins can trigger a manual news refresh from the
            Admin Portal.
          </p>
        </section>

        <section id="community" className="docs-section">
          <h2>Community & Communication</h2>
          <h3>Message Board</h3>
          <p>
            <Link to="/message-board" className="docs-link">Message Board</Link> is the community discussion area.
            Post questions, share updates, and reply to other owners. Admins can moderate content. Enable message
            notifications in Preferences to receive email when someone replies to your post.
          </p>
          <h3>Polls</h3>
          <p>
            <Link to="/polls" className="docs-link">Polls</Link> let the HOA gather owner input. Each lot gets one
            vote per open poll. Results update as votes are cast. Closed polls remain visible for reference.
          </p>
          <h3>Emergency message</h3>
          <p>
            Administrators can broadcast an urgent community message from the Admin Portal. When active, a banner
            appears across the site and eligible owners receive notification per their preferences.
          </p>
        </section>

        <section id="hoa" className="docs-section">
          <h2>HOA Records & Documents</h2>
          <h3>My Invoices</h3>
          <p>
            <Link to="/invoices" className="docs-link">My Invoices</Link> lists HOA assessments and fees tied to
            your lot(s). You can pay open invoices with a saved card through Stripe. Payment history and receipt
            details are shown per invoice.
          </p>
          <h3>Documents</h3>
          <p>
            <Link to="/hoa-docs" className="docs-link">Documents</Link> stores governing documents, policies, and
            shared HOA files. Browse folders and open PDFs or downloads directly in the browser.
          </p>
          <h3>Owner Contacts & property lines</h3>
          <p>
            <Link to="/owners" className="docs-link">Owner Contacts</Link> is the directory of lot owners (contact
            info as provided to the HOA). When parcel data is linked, each lot also shows:
          </p>
          <ul>
            <li><strong>Property / GIS</strong> — PIN, acreage, and links to the county map and tax record for that parcel</li>
            <li><strong>View lines</strong> — expands an inline boundary map (assessor data, not a survey)</li>
          </ul>
          <p>
            Administrators sync assessor GIS data from the <strong>Admin Portal</strong> with{' '}
            <strong>Sync all parcels from assessor</strong>. Use the map viewer for fence and landscaping planning;
            consult a licensed surveyor for legal boundaries.
          </p>
          <h3>Contractors</h3>
          <p>
            <Link to="/contractors" className="docs-link">Contractors</Link> lists board-approved vendors with
            owner star ratings and reviews — landscapers, HVAC, builders, and similar services. The{' '}
            <strong>Other reviews</strong> column links to each contractor&apos;s profiles on Google, Yelp, BBB, Angi,
            and Facebook (refreshed nightly).
          </p>
          <h3>Governance & compliance (administrators)</h3>
          <p>
            <Link to="/admin-compliance" className="docs-link">Governance & Compliance</Link> helps the board stay current
            with recurring legal obligations — annual meetings, director elections, budget adoption, insurance renewals,
            tax filings, and records requests. Generate a fiscal-year checklist, log meeting notices (with short-notice
            warnings), schedule elections, and certify results. Not legal advice — confirm deadlines with your attorney
            and bylaws.
          </p>
          <h3>HOA finance (administrators)</h3>
          <p>
            Treasurers and board members with admin access use{' '}
            <Link to="/admin-finance" className="docs-link">HOA Finance</Link> for accounts payable: track HOA
            operating and reserve bank balances, enter vendor bills tied to the contractor directory, route bills
            through draft → approval → payment, and record checks or ACH. Owner assessments and Stripe collections
            remain on <Link to="/admin-invoices" className="docs-link">Manage Invoices</Link>.
          </p>
          <h3>Governance calendar</h3>
          <p>
            <Link to="/compliance-calendar" className="docs-link">Governance Calendar</Link> shows upcoming owner-visible
            meetings and active board elections (nomination deadlines, open seats). This complements the static{' '}
            <Link to="/calendar" className="docs-link">Community Calendar</Link> for social events.
          </p>
          <h3>Board members & meeting minutes</h3>
          <p>
            Board rosters and archived meeting minutes are available at{' '}
            <Link to="/board-members" className="docs-link">/board-members</Link> and{' '}
            <Link to="/meeting-minutes" className="docs-link">/meeting-minutes</Link> (linked from Documents or
            direct URL).
          </p>
          <h3>Calendar</h3>
          <p>
            <Link to="/calendar" className="docs-link">Calendar</Link> shows HOA meetings, community events, and
            curated local happenings across Western NC. Use <strong>Find Local Events</strong> to pull suggestions
            for the next few months.
          </p>
          <h3>Rentals</h3>
          <p>
            <Link to="/rentals" className="docs-link">Rentals</Link> lists properties owners have chosen to
            advertise within the community.
          </p>
        </section>

        <section id="access" className="docs-section">
          <h2>Gate Access & Mail</h2>
          <h3>Gate Access</h3>
          <p>
            <Link to="/gate-access" className="docs-link">Gate Access</Link> lets you register license plates for
            automatic entry at the community gate. Each plate is tied to your lot.
          </p>
          <ul>
            <li><strong>Add a plate</strong> — enter the plate number and optional label (e.g. &quot;Guest SUV&quot;).</li>
            <li><strong>Open gate now</strong> — remotely trigger the gate for an immediate visit.</li>
            <li><strong>Access log</strong> — recent gate events (LPR, manual opens, gate-camera vehicle sightings, auto-open for recognized vehicles). The list refreshes automatically.</li>
          </ul>
          <p>
            Plates are matched by the gate camera and LPR system. Administrators also review vehicle sightings under{' '}
            <strong>Admin Portal → Vehicle Log</strong>. Keep registrations current and remove vehicles you no longer use.
          </p>
          <h3>Mail Delivery</h3>
          <p>
            <Link to="/mailbox" className="docs-link">Mail Delivery</Link> shows whether mail was detected at the
            community mailbox cluster. When Ring cameras are linked, motion snapshots are analyzed to detect
            delivery activity. You will see today&apos;s status, recent history, and detection images when available.
          </p>
          <p>
            The dashboard also shows a <strong>Mail Delivery</strong> summary widget when monitoring is active.
          </p>
        </section>

        <section id="monitoring" className="docs-section">
          <h2>Monitoring & Environment</h2>
          <h3>Well Monitoring</h3>
          <p>
            <Link to="/well-monitoring" className="docs-link">Well Monitoring</Link> charts community well levels
            and related metrics over time so owners can track groundwater trends.
          </p>
          <h3>Seismic Activity</h3>
          <p>
            <Link to="/seismic-monitoring" className="docs-link">Seismic Activity</Link> displays recent earthquake
            data relevant to the region from USGS feeds.
          </p>
          <h3>Road Conditions</h3>
          <p>
            <Link to="/road-conditions" className="docs-link">Road Conditions</Link> aggregates NCDOT and local road
            status — useful during winter weather or construction.
          </p>
          <h3>Local River Info</h3>
          <p>
            <Link to="/local-river-info" className="docs-link">Local River Info</Link> shows Nantahala and nearby
            river levels, flow, and safety context for paddlers and property owners.
          </p>
          <h3>Weather alerts</h3>
          <p>
            When NWS watches or warnings affect the community zones, a compact alert strip appears below the header.
            Tap <strong>Details</strong> to expand, <strong>Radar</strong> for a map overlay (close anytime), or
            <strong> Dismiss</strong> to hide the alert for your current session. The portal keeps working — alerts
            never block navigation or forms. New bulletins reappear automatically.
          </p>
        </section>

        <section id="identification" className="docs-section">
          <h2>Identification Tools</h2>
          <p>
            Three AI-assisted tools help identify species and specimens found around the resort. Upload or photograph
            a subject and receive a best-guess identification with notes.
          </p>
          <ul>
            <li>
              <Link to="/plant-tree-identification" className="docs-link">Plant and Tree Identification</Link> —
              leaves, flowers, bark, and growth habit.
            </li>
            <li>
              <Link to="/animal-identification" className="docs-link">Animal and Wildlife Identification</Link> —
              mammals, birds, reptiles, and insects.
            </li>
            <li>
              <Link to="/rock-mineral-identification" className="docs-link">Rock and Mineral Identification</Link> —
              stones and minerals from the Southern Appalachians.
            </li>
          </ul>
          <p>
            Identifications are saved to community galleries where you can browse prior entries. Results are
            educational, not authoritative — verify before foraging or handling wildlife.
          </p>
        </section>

        <section id="maps" className="docs-section">
          <h2>Maps, Aviation & Night Sky</h2>
          <h3>Neighborhood Map</h3>
          <p>
            <Link to="/neighborhood-map" className="docs-link">Neighborhood Map</Link> is an interactive map of
            lots, roads, and landmarks. Available before and after login; some layers may require owner sign-in.
          </p>
          <h3>Flight Radar</h3>
          <p>
            <Link to="/flight-radar" className="docs-link">Flight Radar</Link> shows aircraft near the resort using
            live ADS-B data. Tap a flight for details and optional aircraft photos.
          </p>
          <h3>Star Map</h3>
          <p>
            <Link to="/star-map" className="docs-link">Star Map</Link> is a night-sky view for the resort
            coordinates — constellations, planets, satellites, and astronomy events. Best used on a clear night away
            from porch lights.
          </p>
        </section>

        <section id="media" className="docs-section">
          <h2>Photos & Video</h2>
          <h3>Community Pics</h3>
          <p>
            <Link to="/community-pics" className="docs-link">Community Pics</Link> is a shared photo gallery.
            Upload scenery, wildlife, and event photos. Tap any image for detail view and comments.
          </p>
          <h3>Onsite Webcam</h3>
          <p>
            <Link to="/video" className="docs-link">Onsite Webcam</Link> streams the property webcam when online.
            Some streams may be restricted to owners.
          </p>
        </section>

        <section id="mobile" className="docs-section">
          <h2>Mobile App</h2>
          <p>
            NMR offers optional iOS and Android companion apps with the same owner account. Install links are on the
            main page and in your preferences. The mobile-friendly website includes full owner features; native apps
            are a separate community deployment add-on for HOAs that want App Store / Play distribution.
          </p>
          <p>
            The mobile drawer mirrors the website menu: Home, community features, monitoring tools, and (for admins)
            the Admin Portal. Biometric login may be available on supported devices after your first password sign-in.
          </p>
        </section>

        <section id="support" className="docs-section">
          <h2>Support</h2>
          <h3>Ask Nanny</h3>
          <p>
            Tap <strong>Ask Nanny</strong> (bottom-right on any signed-in page) for instant help with portal features.
            Nanny is named after the <strong>Nantahala River</strong> — the mountain river at the heart of our
            community. Ask her why, or for history on Nantahala (Cherokee name, gorge, national forest, and river).
            She also answers owner questions about gate access, invoices, mail, polls, and more. Administrators get
            additional guidance for the Admin Portal and ops tools. Nanny can open the gate when you ask, and supports
            voice input and read-aloud replies in supported browsers.
          </p>
          <p>
            For account access, lot assignment, or billing questions, contact the HOA board or property manager.
            Technical issues with the portal can be reported to the site administrator.
          </p>
          <p>
            Read our <Link to="/privacy" className="docs-link">Privacy Policy</Link> for how community data is
            handled. Administrators manage users, invoices, HOA finance (vendor AP), governance compliance, polls,
            mailbox setup, parcel GIS sync, and vehicle logging from the <strong>Admin Portal</strong> (admin accounts only).
          </p>
          <p>
            Boards evaluating this platform for another community can review pricing and integrations at{' '}
            <Link to="/for-communities" className="docs-link">/for-communities</Link> (AI management, smart-home
            add-ons, mobile apps, and PMC replacement checklist).
          </p>
        </section>

        <footer className="docs-footer">
          <p>Last updated June 28, 2026 · Nantahala Mountain Resort HOA</p>
        </footer>
      </article>
    </div>
  );
}

export default HelpPage;